import {I18n} from '@aws-amplify/core';
import {Code, Group, ScrollArea, Stack, Text, Title} from '@mantine/core';
import {
    PlatformPropertyCard,
    PlatformPropertyCardCopyButtonDecorator,
    PlatformPropertyCardEntryInput,
} from '@flexinet/ui-components';

import {ReconciliationReport} from '../types';
import {ChannelBadge, DiscrepancyBadge} from './Badges.tsx';
import {prettyPrintDiscrepancyDetails} from '../utils/parseDiscrepancyDetails.ts';

export const Profile = ({record}: { record: ReconciliationReport }) => {

    // Typed, not cast (§13): a cast to PlatformPropertyCardProperty[] is what hid the
    // invalid `formatAs` key that printed these timestamps as raw ISO (FNP-1891).
    const summaryProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('ID'),
            value: record.id,
            formatter: 'code',
            decorator: (value: string) => <PlatformPropertyCardCopyButtonDecorator value={value}/>,
            span: 12,
        },
        {
            label: I18n.get('Channel'),
            value: <ChannelBadge channel={record.channel}/>,
            span: 6,
        },
        {
            label: I18n.get('Status'),
            value: <DiscrepancyBadge record={record}/>,
            span: 6,
        },
        {
            label: I18n.get('Run Date'),
            value: record.runDate,
            emptyAs: 'dash',
            span: 6,
        },
        {
            label: I18n.get('Run Started At'),
            value: record.runStartedAt,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 6,
        },
        {
            // Absent while a run is still in progress: a dash, never a fabricated time.
            label: I18n.get('Run Completed At'),
            value: record.runCompletedAt,
            formatter: 'timestamp',
            emptyAs: 'dash',
            span: 6,
        },
    ];

    const outcomeProperties: PlatformPropertyCardEntryInput[] = [
        {
            label: I18n.get('Ours But Not Theirs'),
            value: record.oursButNotTheirsCount ?? 0,
            span: 4,
        },
        {
            label: I18n.get('Theirs But Not Ours'),
            value: record.theirsButNotOursCount ?? 0,
            span: 4,
        },
        {
            label: I18n.get('Quantity Mismatch'),
            value: record.quantityMismatchCount ?? 0,
            span: 4,
        },
    ];

    const details = prettyPrintDiscrepancyDetails(record.discrepancyDetails);

    return (
        <Stack gap={'lg'}>
            <PlatformPropertyCard properties={summaryProperties}/>
            <PlatformPropertyCard properties={outcomeProperties}/>

            <Stack gap={'xs'}>
                <Title order={5}>{I18n.get('Discrepancy details')}</Title>
                {details
                    ? <ScrollArea.Autosize mah={400}>
                        <Code block>{details}</Code>
                    </ScrollArea.Autosize>
                    : <Text c={'dimmed'} size={'sm'}>{I18n.get('No discrepancy details for this run.')}</Text>}
                {record.discrepancyDetailsS3Uri
                    ? <Stack gap={4}>
                        <Text size={'xs'} c={'dimmed'}>{I18n.get('Full discrepancies overflowed inline storage:')}</Text>
                        {/* FNP-593: an s3:// URI has no browser handler, so an Anchor href
                            was a dead link. Render it as copyable monospace text instead —
                            the operator can paste it into the AWS console / CLI. */}
                        <Group gap={'xs'} wrap={'nowrap'} align={'center'}>
                            <Code>{record.discrepancyDetailsS3Uri}</Code>
                            <PlatformPropertyCardCopyButtonDecorator value={record.discrepancyDetailsS3Uri}/>
                        </Group>
                    </Stack>
                    : null}
            </Stack>
        </Stack>
    );
};
